'use client';

import { useForm, SubmitHandler } from 'react-hook-form';
import { AxiosError } from 'axios';
import { SetStateAction, useState } from 'react';
import Input from '@/components/Input/Input';
import PhoneNumberUnsubscribe from './PhoneNumber';
import { SmallLoader } from '@/components';
import { SalesforceService } from '@/lib/Services/SalesforceService';
import { UnsubscribeRequestBody } from '@/types/salesforce';
import { fieldRules } from '@/utils/form';
import styles from './Form.module.scss';

export interface UnsubscribeFormData {
  email: string;
  firstName: string;
  lastName: string;
  phone: string;
  unsubscribe: boolean;
  optOut: boolean;
}

interface Props {
  setSuccess: React.Dispatch<SetStateAction<boolean>>;
}

const Form = ({ setSuccess }: Props) => {
  const {
    register,
    handleSubmit,
    formState: { errors },
    getValues,
  } = useForm<UnsubscribeFormData>();
  const [pendingInfo, setPendingInfo] = useState({
    loading: false,
    error: '',
  });

  const onSubmit: SubmitHandler<UnsubscribeFormData> = async (
    info: UnsubscribeFormData
  ) => {
    setPendingInfo({ loading: true, error: '' });
    setSuccess(false);
    try {
      const reqBody: UnsubscribeRequestBody = {
        mobilePhone: info.phone,
        ...(info.optOut ? { optOut: 'Yes' as const } : {}),
        unsubscribe: info.unsubscribe,
      };
      await SalesforceService.unsubscribe(reqBody);

      setPendingInfo({ loading: false, error: '' });
      setSuccess(true);
    } catch (e) {
      const err = e as AxiosError<string, string>;
      setPendingInfo({
        loading: false,
        error: err.response?.data || 'Something went wrong. Try again later.',
      });
      setSuccess(false);
    }
  };

  const values = getValues();

  return (
    <form className={styles.form} onSubmit={handleSubmit(onSubmit)}>
      <div className={styles.inputRow}>
        <Input
          label="First name"
          type="text"
          id="firstName"
          placeholder="Your first name"
          register={register('firstName', fieldRules.first_name)}
          error={errors.firstName}
        />
        <Input
          label="Last name"
          type="text"
          id="lastName"
          placeholder="Your last name"
          register={register('lastName', fieldRules.last_name)}
          error={errors.lastName}
        />
      </div>
      <div className={styles.inputRow}>
        <Input
          label="Email"
          type="email"
          id="email"
          placeholder="Your email"
          register={register('email', fieldRules.email)}
          error={errors.email}
        />
        <PhoneNumberUnsubscribe
          register={register}
          errors={errors}
          phoneValue={values.phone}
        />
      </div>
      <div className={styles.inputRow}>
        <div className={styles.checkbox_wrapper}>
          <label className={styles.label} htmlFor="unsubscribe">
            <input
              id="unsubscribe"
              type="checkbox"
              className={styles.checkbox}
              {...register('unsubscribe')}
            />
            Unsubscribe from email communications
          </label>
          <label className={styles.label} htmlFor="optOut">
            <input
              id="optOut"
              type="checkbox"
              className={styles.checkbox}
              {...register('optOut')}
            />
            Opt-out of information sharing
          </label>
        </div>
        <div className={styles.buttonWrapper}>
          {pendingInfo.error.length > 0 && (
            <span className={styles.error}>{pendingInfo.error}</span>
          )}
          <button
            disabled={pendingInfo.loading}
            className={styles.button}
            type="submit"
          >
            <div className={styles.button_text_wrapper}>
              <span>Confirm my selection</span>
              <span>Confirm my selection</span>
            </div>
            {pendingInfo.loading && <SmallLoader />}
          </button>
        </div>
      </div>
    </form>
  );
};

export default Form;
